import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useSite } from "../context/SiteContext";

export const LanguageSwitcher = ({ variant = "light", testId = "language-switcher" }) => {
  const { t } = useTranslation();
  const { languages, lang, setLanguage } = useSite();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const current = languages.find((l) => l.code === lang);
  const list = useMemo(
    () => languages.filter((l) => `${l.native} ${l.name} ${l.code}`.toLowerCase().includes(q.toLowerCase())),
    [languages, q]
  );
  const triggerCls =
    variant === "dark"
      ? "text-[var(--cream)] border-white/25 hover:border-white/60"
      : "text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          aria-label={t("nav.choose_language")}
          className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${triggerCls}`}
        >
          <Globe size={16} aria-hidden="true" />
          <span>{current?.native || lang}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2 rounded-2xl">
        <p className="px-2 pt-1 pb-2 text-xs eyebrow">{t("nav.choose_language")}</p>
        {languages.length > 8 && (
          <input
            className="field-input py-2 text-sm mb-2"
            placeholder={t("nav.search_language")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="language-search-input"
          />
        )}
        <ul className="max-h-72 overflow-auto" role="listbox" aria-label={t("nav.language")}>
          {list.map((l) => (
            <li key={l.code}>
              <button
                role="option"
                aria-selected={l.code === lang}
                data-testid={`lang-option-${l.code}`}
                dir={l.dir}
                onClick={async () => {
                  await setLanguage(l.code);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between rounded-xl px-3 py-2 text-sm hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                <span>
                  {l.native} <span className="text-[hsl(var(--muted-foreground))] text-xs">· {l.name}</span>
                </span>
                {l.code === lang && <Check size={14} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
